import "./style.css";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import Button from "../../Button/Button";
import { CreateAppointmentParams, getAvailabilityForAppointment, postAppointment } from "../../api/appointmentApi";
import { saveAppointmentBundle } from "../../ducks/redux/actions/userChoices";
import NoSuitableAppointmentModal from "../Modal/NoSuitableAppointmentModal";
import ExitModalAndSave from "../Modal/ExitModalAndSave";
import { isUndefinedNullOrEmpty } from "../../utils/translation/helper";
import { isNullOrUndefined } from "../../utils/formatting";
import LoadingSpinner from "../../storybook/LoadingSpinner";
import { nanoid } from "nanoid";
import * as api from "../../api";
import { NoteType } from "../../models/Repair/Repair";
import { Helper } from "../../utils/Helper/HelperUtil";
import { AppointmentAvailability, AvailabilityBand, Bundle, CalendarPayload, WOEnvelope } from "../../models/Repair/Appointment";






const AppointmentScheduler: React.FC<any> = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const appointmentAvailabilityResponses: Bundle | null = useSelector((s: any) => s?.userChoicesReducer?.appointmentBundle ?? null);
  const workOrderObject = useSelector((s: any) => s?.addresses?.workOrderRepair);
  const dmcWorkOrderObject = useSelector((s: any) => s?.addresses?.dmcWorkOrderRepair);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isConfirmAppointmentsLoading, setIsConfirmAppointmentsLoading] = useState(false);

  const hasPresentableAvailabilities = (wo?: WOEnvelope) =>
    wo?.isAppointable &&
    Array.isArray(wo?.appointmentAvailability?.availabilities) &&
    (wo?.appointmentAvailability?.availabilities?.length || 0) > 0 &&
    Array.isArray(wo?.appointmentAvailability?.availabilities?.[0]?.slots) &&
    (wo?.appointmentAvailability?.availabilities?.[0]?.slots?.length || 0) > 0;

  const [step, setStep] = useState<"original" | "dmc" | null>(null);

  const [selectedOriginalWOSlot, setSelectedOriginalWOAppSlot] = useState<any>(null);
  const [selectedDmWOSlot, setSelectedDmWOAppSlot] = useState<any>(null);
  const [selectedOriginalKey, setSelectedOriginalKey] = useState<string | null>(null);
  const [selectedDmcKey, setSelectedDmcKey] = useState<string | null>(null);

  const [trackOriginal, setTrackOriginal] = useState(0);
  const [trackDmc, setTrackDmc] = useState(0);
  const range = 5;
  const [showNoSuitableModal, setShowNoSuitableModal] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [showOriginalRepairCalendar, setShowOriginalRepairCalendar] = useState(false);
  const [showDMRepairCalendar, setShowDMRepairCalendar] = useState(false);
  const maxAppointmentTimeInSec = useSelector((state: any) => state?.userChoicesReducer?.availabilityWaitTimeSeconds);
  const userChosenSOR = useSelector((state: any) => state?.userChoicesReducer?.userChosenSOR);
  const selectedRepairReasonObject = useSelector((state: any) => state?.dashboardReducer?.formState?.selectedRepairReasonObject);

  const tConv24 = (time24: string) => {
    const H = +time24.slice(0, 2);
    const h = H % 12 || 12;
    const ampm = H < 12 ? "am" : "pm";
    return `${h}${ampm}`;
  };

  const fmtDate = (isoDate: string) =>
    new Date(isoDate).toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  const buildCalendar = (appointmentAvailability?: AppointmentAvailability | null): CalendarPayload => {
    return {
      availabilities: (appointmentAvailability?.availabilities as AvailabilityBand[] | undefined) ?? undefined,
      isPreferences: !!appointmentAvailability?.isPreferences,
      hasSlaAvailability: !!appointmentAvailability?.hasSlaAvailability,
      earliestAvailabilityType: appointmentAvailability?.earliestAvailabilityType ?? undefined,
    };
  };

  const [origCalendar, setOrigCalendar] = useState<CalendarPayload>();
  const [dmcCalendar, setDMCCalendar] = useState<CalendarPayload>();
  

  const fetchAvailabilityForWorkOrder = async (dmcWOId: any, dmcJob: any, contractorDisplayName: string, preOperationKey?: string) => {
    const operationKey = preOperationKey || nanoid();
    const timeoutMs = Number(maxAppointmentTimeInSec) * 1000;
    const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("TIMEOUT")), timeoutMs));

      const priorityWorkProgramme = userChosenSOR === ''
                  ? selectedRepairReasonObject?.[0]?.sors?.[0]?.workProgramme
                  : selectedRepairReasonObject?.[0]?.sors?.find((sor: any) => sor?.sorCode === userChosenSOR)?.workProgramme;

                 
    try {
      const availability: any = await Promise.race([
        getAvailabilityForAppointment(dmcWOId, (dmcJob || "").trim(), operationKey, contractorDisplayName || "", priorityWorkProgramme??workOrderObject?.workProgramme),
        timeoutPromise
      ]);
      const status = availability?.status ?? availability?.statusCode ?? availability?.data?.statusCode;
      const failureReason = status !== 200 ? "failure" : "availability_returned";
      if (typeof status === "number") {
        const reason = status === 404 &&  (availability?.title || "").trim().toLowerCase().includes(t("APPT_NoAvailability_APIResponse").trim().toLowerCase()) ? "no_availability" : failureReason;
        return { operationKey, availability: reason ? null : availability, reason };
      }
      return { operationKey, availability, reason: "success" };
    } catch (e: any) {
      return { operationKey, availability: null, reason: e?.message === "TIMEOUT" ? "timeout" : "failure" };
    }
  };

  const postUnappointedNote = (jobNo: string, text: string) => {
    const note = text === "timeout" ? t("WO_Note_Timeout") :
      text === "failure" ? t("WO_Note_Failure") :
        t("WO_Note_No_Availability");
    const [s, q] = (jobNo || "").split("/", 2).map(x => x?.trim());
    if (s && q) api.postNoteForWorkorder(s, q, { noteTypeId: NoteType.Other, text: note }).catch(() => { });
  };

  const retrieveAppointments = async (appointmentAvailabilityResponsesBundle: any) => {

    const checkAvailabilityOriginalWO = checkAvailability(appointmentAvailabilityResponsesBundle?.originalWO),
      checkAvailabilityDMCWO = checkAvailability(appointmentAvailabilityResponsesBundle?.dmc),
      originalOpKey = nanoid(),
      dmcOpKey = nanoid();

    if (!checkAvailabilityOriginalWO && !checkAvailabilityDMCWO) {
      //Skip to Final page if none of the repairs require appointing
      //Added as a failsafe in case user force navigates to this page
      history.push("/RepairFinalDetails");
      return;
    }

    setStep(checkAvailabilityOriginalWO ? "original" :"dmc");
    let originalPromise: Promise<any> | null = null;
    if (checkAvailabilityOriginalWO) {
      originalPromise = fetchAvailabilityForWorkOrder(
        appointmentAvailabilityResponsesBundle?.originalWO?.id, appointmentAvailabilityResponsesBundle?.originalWO?.jobNo,
        appointmentAvailabilityResponsesBundle?.originalWO?.contractor?.name,
        appointmentAvailabilityResponsesBundle?.originalWO?.operationKey
      );
    }

    let dmcPromise: Promise<any> | null = null;
    if (checkAvailabilityDMCWO) {
      dmcPromise = fetchAvailabilityForWorkOrder(
        appointmentAvailabilityResponsesBundle?.dmc?.id, appointmentAvailabilityResponsesBundle?.dmc?.jobNo,
        appointmentAvailabilityResponsesBundle?.dmc?.contractor?.name,
        appointmentAvailabilityResponsesBundle?.dmc?.operationKey
      );
    }

    const results = await Promise.allSettled([originalPromise || Promise.resolve(null), dmcPromise || Promise.resolve(null)]);
    let originalAvailabilityResult: any = null;
    let dmcAvailabilityResult: any = null;

    if (originalPromise != null && results[0]?.status === "fulfilled") {
      originalAvailabilityResult = results[0].value;
    }

    if (dmcPromise != null && results[1]?.status === "fulfilled") {
      dmcAvailabilityResult = results[1].value;
    }

    const originalAvailability = originalAvailabilityResult?.availability;
    const dmcAvailability = dmcAvailabilityResult?.availability;
    // Notes for Appointment not retrieved scenarios - Original WO
    if (!isUndefinedNullOrEmpty(originalAvailability?.availabilities)) {
      setOrigCalendar(buildCalendar(originalAvailability));
    }
    else if(checkAvailabilityOriginalWO) {
      postUnappointedNote(appointmentAvailabilityResponsesBundle?.originalWO?.jobNo, originalAvailabilityResult?.reason);
    }

    // Notes for Appointment not retrieved scenarios - DM WO
    if (!isUndefinedNullOrEmpty(dmcAvailability?.availabilities)) {
      setDMCCalendar(buildCalendar(dmcAvailability));
    }
    else if (checkAvailabilityDMCWO) {
      postUnappointedNote(appointmentAvailabilityResponsesBundle?.dmc?.jobNo, dmcAvailabilityResult?.reason);
    }

    if (isUndefinedNullOrEmpty(originalAvailability?.availabilities) && isUndefinedNullOrEmpty(dmcAvailability?.availabilities)) {
      //Skip to Final page if none of the repairs returned successful calendar avaliabilities
      history.push("/RepairFinalDetails");
      return;
    }

    const appointmentBundle = Helper.buildAppointmentBundle({
      original: isUndefinedNullOrEmpty(appointmentAvailabilityResponsesBundle?.originalWO) ? null : {
        id: appointmentAvailabilityResponsesBundle?.originalWO?.id,
        contractorCode: appointmentAvailabilityResponsesBundle?.originalWO?.contractor?.code,
        contractorName: appointmentAvailabilityResponsesBundle?.originalWO?.contractor?.name,
        jobNo: (appointmentAvailabilityResponsesBundle?.originalWO?.jobNo || "").trim(),
        operationKey: appointmentAvailabilityResponsesBundle?.originalWO?.operationKey || originalOpKey,
        isAppointable: appointmentAvailabilityResponsesBundle?.originalWO?.isAppointable,
        isAppointmentRetrievalFailed: checkAvailabilityOriginalWO && isUndefinedNullOrEmpty(originalAvailability?.availabilities),
        calendar: {
          availabilities: originalAvailability?.availabilities ?? null,
          isPreferences: originalAvailability?.isPreferences,
          hasSlaAvailability: originalAvailability?.hasSlaAvailability,
          earliestAvailabilityType: originalAvailability?.earliestAvailabilityType,
          availabilityStatusReason: originalAvailability?.reason
        }
      },
      dmc: isUndefinedNullOrEmpty(appointmentAvailabilityResponsesBundle?.dmc) ? null : {
        id: appointmentAvailabilityResponsesBundle?.dmc?.id,
        contractorCode: appointmentAvailabilityResponsesBundle?.dmc?.contractor?.code,
        contractorName: appointmentAvailabilityResponsesBundle?.dmc?.contractor?.name,
        jobNo: (appointmentAvailabilityResponsesBundle?.dmc?.jobNo || "").trim(),
        operationKey: appointmentAvailabilityResponsesBundle?.dmc?.operationKey || dmcOpKey,
        isAppointable: appointmentAvailabilityResponsesBundle?.dmc?.isAppointable,
        isAppointmentRetrievalFailed: checkAvailabilityDMCWO && isUndefinedNullOrEmpty(dmcAvailability?.availabilities),
        calendar: {
          availabilities: dmcAvailability?.availabilities ?? null,
          isPreferences: dmcAvailability?.isPreferences,
          hasSlaAvailability: dmcAvailability?.hasSlaAvailability,
          earliestAvailabilityType: dmcAvailability?.earliestAvailabilityType,
          availabilityStatusReason: dmcAvailabilityResult?.reason
        }
      }
    });

    return appointmentBundle;

  }

  const checkAvailability = (wo: any) => {
    return wo?.isAppointable && !wo?.isAppointmentRetrievalFailed && isNullOrUndefined(wo?.appointmentAvailability)
  }

  useEffect(() => {
    (async () => {
      if (appointmentAvailabilityResponses && isLoading) {
        const appointmentAvailabilities = await retrieveAppointments(appointmentAvailabilityResponses);
        const hasOriginalWOAvailability = hasPresentableAvailabilities(appointmentAvailabilities?.originalWO);
        const hasDMWOAvailability = hasPresentableAvailabilities(appointmentAvailabilities?.dmc);

        setShowOriginalRepairCalendar(!!hasOriginalWOAvailability);
        setShowDMRepairCalendar(!!hasDMWOAvailability);


        if (hasOriginalWOAvailability || hasDMWOAvailability) {
          setStep(hasOriginalWOAvailability ? "original" : "dmc");
          setIsLoading(false);
          return;
        } else {
          history.push("/RepairFinalDetails");
        }
        dispatch(saveAppointmentBundle(appointmentAvailabilities));
      }
    })()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentAvailabilityResponses]);

  const changeCalPage = (back: boolean, calendar: CalendarPayload | null, woType: "original" | "dmc") => {
    setError("");
    const maxLen = calendar?.availabilities?.[0]?.slots?.length || 0;
    const current = woType === "original" ? trackOriginal : trackDmc;
    const next = back ? Math.max(0, current - range) : current + range;
    if (back) {
      if (woType === "original") setTrackOriginal(next);
      else setTrackDmc(next);
      return;
    }
    if (next >= maxLen) return;
    if (woType === "original") setTrackOriginal(next);
    else setTrackDmc(next);
  };

  const slotKeyFromParts = (date: string, availabilityType: string, start: string, end: string) =>
    `${date}|${availabilityType}|${start}-${end}`;

  const onPickSlot = (slot: any, woType: "original" | "dmc") => {
    setError("");
    const key = slotKeyFromParts(slot.date, slot.availabilityType, slot.start, slot.end);
    if (woType === "original") {
      if (selectedOriginalKey === key) {
        setSelectedOriginalKey(null);
        setSelectedOriginalWOAppSlot(null);
      } else {
        setSelectedOriginalKey(key);
        setSelectedOriginalWOAppSlot(slot);
      }
    } else {
      if (selectedDmcKey === key) {
        setSelectedDmcKey(null);
        setSelectedDmWOAppSlot(null);
      } else {
        setSelectedDmcKey(key);
        setSelectedDmWOAppSlot(slot);
      }
    }
  };

  const continueToNextPage = async (noSuitableAppointment: boolean = false) => {
    let noSuitableApptOriginalWO = false, noSuitableApptDMWO = false;
    if (noSuitableAppointment) {
      setShowNoSuitableModal(false);
      if (step === "original") {
        setSelectedOriginalWOAppSlot(null);
        setSelectedOriginalKey(null);
        noSuitableApptOriginalWO = true;
      } else {
        setSelectedDmWOAppSlot(null);
        setSelectedDmcKey(null);
        noSuitableApptDMWO = true;
      }

      if ((noSuitableApptOriginalWO && !showDMRepairCalendar) || (isUndefinedNullOrEmpty(selectedOriginalWOSlot) && noSuitableApptDMWO)) {
        setIsConfirmAppointmentsLoading(false);
        history.push("/repairfinaldetails");
        return;
      }
    } else if ((step === "original" && !selectedOriginalWOSlot) || (step == "dmc" && !selectedDmWOSlot)) {
      setError(t("CPR049"));
      return;
    }

    if (showOriginalRepairCalendar && showDMRepairCalendar && step == "original") {
      continueToBookDMAppointment();
    } else {
      await bookAllSelectedAppointments(noSuitableApptOriginalWO, noSuitableApptDMWO);
    }
  };

  const continueToBookDMAppointment = () => {
    if (isConfirmAppointmentsLoading) return;
    setError("");
    setStep("dmc");
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 600);
  };

  const bookAllSelectedAppointments = async (noSuitableApptOriginalWO: boolean, noSuitableApptDMWO: boolean) => {
    setError("");
      const priorityWorkProgramme = userChosenSOR === ''
                  ? selectedRepairReasonObject?.[0]?.sors?.[0]?.workProgramme
                  : selectedRepairReasonObject?.[0]?.sors?.find((sor: any) => sor?.sorCode === userChosenSOR)?.workProgramme;

     try {
      if (isConfirmAppointmentsLoading) return;
      setIsConfirmAppointmentsLoading(true);
      const tasks: Promise<any>[] = [];

      if (selectedOriginalWOSlot && !noSuitableApptOriginalWO) {
        tasks.push(bookWorkOrder(appointmentAvailabilityResponses!.originalWO!, selectedOriginalWOSlot, priorityWorkProgramme??workOrderObject?.workProgramme));
      }

      if (selectedDmWOSlot && !noSuitableApptDMWO) {
        tasks.push(bookWorkOrder(appointmentAvailabilityResponses!.dmc!, selectedDmWOSlot,priorityWorkProgramme??workOrderObject?.workProgramme));
      }
      await Promise.all(tasks);

      dispatch(
        saveAppointmentBundle({
          ...appointmentAvailabilityResponses,
          originalWO: appointmentAvailabilityResponses?.originalWO ? { ...(appointmentAvailabilityResponses!.originalWO), appointmentSlotSelected: !noSuitableApptOriginalWO ? selectedOriginalWOSlot : undefined } : undefined,
          dmc: appointmentAvailabilityResponses?.dmc ? { ...(appointmentAvailabilityResponses!.dmc), appointmentSlotSelected: !noSuitableApptDMWO ? selectedDmWOSlot : undefined } : undefined
        } as any)
      );
    } catch (e: any) {
      console.log(e?.message || t("APPT_Booking_Failed"));
    }
    setIsConfirmAppointmentsLoading(false);
    history.push("/repairfinaldetails");
  };

  const bookWorkOrder = async (wo: WOEnvelope, slot: any, workPrograme: any) => {
    const workOrderId = wo?.id ?? "";
    const contractorName = wo?.contractor?.name ?? "";

    const params: CreateAppointmentParams = {
      isPreferences: !!wo?.appointmentAvailability?.isPreferences,
      jobNumber: wo.jobNo ?? "",
      diaryCode: slot.availabilityType,
      appointmentFromDate: slot.date,
      availabilityType: slot.availabilityType,
      isInsideSla: !!slot.insideSla,
      reasonCode: "NEW_APPOINTMENT",
      remarks: "",
      appointmentToDate: slot.date,
      externalCreateDateTime: slot.date,
      externalReferenceId: "",
      metaData: slot.metadata,
    };

    return postAppointment(workOrderId, params, wo?.operationKey ?? "", contractorName, workPrograme || "");
  };

  const renderCalendar = (calendar: CalendarPayload | null | undefined, woType: "original" | "dmc") => {
    if (!calendar || !Array.isArray(calendar.availabilities) || calendar.availabilities.length === 0) {
      history.push("/LandingView");
      return null;
    }

    const track = woType === "original" ? trackOriginal : trackDmc;
    const total = calendar.availabilities?.[0].slots?.length || 0;
    const showNext = track + range < total;
    const selectedKey = woType === "original" ? selectedOriginalKey : selectedDmcKey;

    return (
      <div className="apScheduler">
        <div className="cal-nav">
          <div>
            {track === 0 ? (
              <span />
            ) : (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a
                className="cal-nav-link"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    changeCalPage(true, calendar, woType);
                  }
                }}
                onClick={() => changeCalPage(true, calendar, woType)}
              >
                <i className="fas fa-chevron-left" /> {t("APPT_Previous_Week")}
              </a>
            )}
          </div>
          <div>
            {showNext ? (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a
                className="cal-nav-link"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    changeCalPage(false, calendar, woType);
                  }
                }}
                onClick={() => changeCalPage(false, calendar, woType)}
              >
                {t("APPT_Next_Week")} <i className="fas fa-chevron-right" />
              </a>
            ) : (
              <span />
            )}
          </div>
        </div>
        <div className="sc-wrapper">
          {calendar.availabilities?.[0]?.slots?.slice(track, track + range).map((_, localIndex: number) => {
            const globalIndex = track + localIndex;
            const dateStr = calendar.availabilities?.[0]?.slots?.[globalIndex]?.date;
            if (!dateStr) return null;
            return (
              <div className="sc-schedules" key={`col-${woType}-${dateStr}-${globalIndex}`}>
                <div className="sc-sched-date sc-mob-date margin-rt">
                  <p className="wd-2">
                    {new Date(dateStr).toDateString().slice(0, 3)}
                    <br />
                    {dateStr.slice(8, 10)}/{dateStr.slice(5, 7)}
                  </p>
                </div>
                <div className="flex flex-row wt1 flex-wrap mt-top row">
                  {calendar.availabilities!.map(
                    ({ availabilityDetails: { start, end, availabilityTypeLabel, availabilityType } }: any, schedIdx: number) => {
                      const slotMeta = calendar?.availabilities?.[schedIdx]?.slots?.[globalIndex];
                      const isNA = slotMeta?.capacity === "Not Available";
                      const key = slotKeyFromParts(dateStr, availabilityType, start, end);
                      const isSelected = selectedKey === key;
                      let cls = "sc-sched-slots cal-btn color-new error-span-remove";
                      if (isNA) {
                        cls = "sc-sched-slots cal-btn disabled-p color_black error-span-remove";
                      } else if (isSelected) {
                        cls = "click-calander sc-sched-slots cal-btn color-new error-span-remove";
                      }
                      return isNA ? (
                        <p className={cls} key={`na-${woType}-${schedIdx}-${globalIndex}`}>
                          -
                        </p>
                      ) : (
                        <button
                          key={`btn-${woType}-${dateStr}-${schedIdx}`}
                          onClick={() =>
                            onPickSlot(
                              {
                                start,
                                end,
                                availabilityTypeLabel,
                                date: dateStr,
                                insideSla: !!slotMeta?.insideSla,
                                availabilityType,
                                metadata: slotMeta?.metadata,
                              },
                              woType
                            )
                          }
                          className={cls}
                          data-testid={`setSelectedSlot_${woType}_${globalIndex}_${schedIdx}`}
                        >
                          <p className="wd-1 text-black">{`${tConv24(start)} - ${tConv24(end)}`}</p>
                        </button>
                      );
                    }
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const originalHeader = (
    <div id="Cal-main-div1" className="my-2">
      <p className="page-title">
        <span className="page-title__light">
          {t("APPT_Header_Repair", { REPAIR: "" }).trim()}
        </span>
        <span className="page-title__strong">
          {workOrderObject?.repairReason || ""}
        </span>
      </p>
      <p className="global-txt mt-4">{t("APPT_Header_Repair_subTitle")}</p>
    </div>
  );

  const dmcHeader = (
    <div id="Cal-main-div2" className="my-4">
      <p className="page-title">
        {t("APPT_Header_DMC_Prefix")}{t("APPT_Header_DMC_Label")}
      </p>
    </div>
  );


  return (
    <div role="main" className="container sm:px-2 ctn-fht">
      <div id="Cal-main-div" className="py-2 h5 my-2">
        <h2 className="visually-hidden">{t("Scheduler_Heading")}</h2>
      </div>

      {isLoading ? (
        <> {step === "original" ? originalHeader : dmcHeader}
          <div className="loader-panel">
            <div className="loader-text">{t("APPT_Loading")}</div>
            <LoadingSpinner />
          </div>
        </>
      ) : isConfirmAppointmentsLoading ? (
        <div className="final-booking-screen" role="status" aria-live="polite">
          <div className="final-booking-inner">

            <p className="final-booking-title">
              {t("APPT_FinalBooking_Title")}
            </p>
            <p className="final-booking-subtitle">
              {t("APPT_FinalBooking_Subtitle")}
            </p>
            <LoadingSpinner />
          </div>
        </div>
      ) : (
        <div>
          {step === "original" && (
            <>
              {originalHeader}
              {selectedOriginalWOSlot && (
                <div className="selected-wrap">
                  <div className="selected-title">{t("APPT_Selected_Label")}</div>
                  <div className="selected-line">
                    <span className="selected-hl">{workOrderObject?.repairReason || t("APPT_Selected_Fallback")}</span>
                    {`${fmtDate(selectedOriginalWOSlot.date)} (${selectedOriginalWOSlot.availabilityTypeLabel || selectedOriginalWOSlot.availabilityType
                      }, ${tConv24(selectedOriginalWOSlot.start)}–${tConv24(selectedOriginalWOSlot.end)})`}
                  </div>
                </div>
              )}
              {renderCalendar(origCalendar, "original")}
            </>
          )}

          {step === "dmc" && (
            <>
              {dmcHeader}
              {<>
                  <div className="selected-wrap">
                    <div className="selected-title">{t("APPT_Selected_Label")}</div>

                    {showOriginalRepairCalendar && (<div className="selected-line">
                      <span className="selected-hl">{workOrderObject?.repairReason || t("RESP")}</span>
                      {
                        selectedOriginalWOSlot ? `${fmtDate(selectedOriginalWOSlot.date)} (${selectedOriginalWOSlot.availabilityTypeLabel || selectedOriginalWOSlot.availabilityType
                          }, ${tConv24(selectedOriginalWOSlot.start)}–${tConv24(selectedOriginalWOSlot.end)})` :
                          t("APPT_No_Appointment_Selected")
                      }
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a
                        className="edit-link"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            setError(""); setShowEditConfirm(true);
                          }
                        }}
                        onClick={() => { setError(""); setShowEditConfirm(true); }}
                      >
                        {t("APPT_Edit")}
                      </a>
                    </div>)}

                    {selectedDmWOSlot && (
                      <div className="selected-line">
                        <span className="selected-hl">{t("BODY_DMC")}</span>
                        {`${fmtDate(selectedDmWOSlot.date)} (${selectedDmWOSlot.availabilityTypeLabel || selectedDmWOSlot.availabilityType
                          }, ${tConv24(selectedDmWOSlot.start)}–${tConv24(selectedDmWOSlot.end)})`}
                      </div>
                    )}
                  </div>

                  {renderCalendar(dmcCalendar, "dmc")}
                </>
              }
            </>
          )}
        </div>
      )}

      {!isLoading && !isConfirmAppointmentsLoading && (
        <div className="actions-bar">
          <span>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              id="btn_nosuitableappoinment"
              className="text-teal cursor-pointer min-w-38 md:min-w-50 global-txt link-plain"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setError("");
                  setShowNoSuitableModal(true);
                }
              }}
              onClick={() => {
                setError("");
                setShowNoSuitableModal(true);
              }}
            >
              {t("No_Suitable_Appointment")}
            </a>
          </span>

          <span className="book-btn">
            <Button id="btn_continue" disabled={
              (step === "original" && !selectedOriginalWOSlot) ||
              (step === "dmc" && !selectedDmWOSlot) ||
              isConfirmAppointmentsLoading} onClick={() => continueToNextPage(false)} className="button-class">
              {t("continue")}
              <i className="fas fa-angle-right relativetext-white pointer-events-none arrow"></i>
            </Button>
          </span>
        </div>
      )}

      {error && (
        <div className="error mb-8" id="custDetails-div5" role="alert">
          <div className="w-full px-2 py-2 bg-yellow flex items-center">
            <div className="px-4 pr-8">
              <i className="fas fa-exclamation-triangle text-brick-70 w-6 h-6"></i>
            </div>
            <div className="flex items-center text-black">
              <p className="w-full text-left my-2 font-AvantGardeGothic-Md">{error}</p>
            </div>
          </div>
        </div>
      )}

      <NoSuitableAppointmentModal
        open={showNoSuitableModal}
        onClose={() => setShowNoSuitableModal(false)}
        onCheckAgain={() => setShowNoSuitableModal(false)}
        onContinueWithout={() => continueToNextPage(true)}
      />

      <ExitModalAndSave
        id="appt-edit-exitmodal"
        open={showEditConfirm}
        modalTitle={<span style={{ color: "#A64CA6", fontWeight: 600 }}>
          {t("APPT_EditConfirm_Title")}
        </span>}
        modalSubtitle={t("APPT_EditConfirm_Subtitle")}
        stayButtonText={t("APPT_EditConfirm_Stay")}
        leaveButtonText={t("APPT_EditConfirm_Leave")}
        closeAfterSaving={false}
        canSaveAndExit={false}
        showBackOnly={true}
        onLeave={() => { setError(""); setStep("original"); setShowEditConfirm(false); }}
        onClose={() => setShowEditConfirm(false)}
      />
    </div>
  );
};

export default AppointmentScheduler;